<template>
  <ul
    :class="
      bem({
        'size': props.size,
        'product-card': props.inProductCard,
        'full-width-swatches': props.fullWidthSwatches,
        'single-line-on-tablet': props.singleLineOnTablet,
        'background': props.background,
        'display': props.display
      })
    "
    :data-limited-count="props.limitedEditionCount"
  >
    <li
      v-for="(variant, index) in props.variants"
      :key="index"
      :data-item="index"
      :class="
        bem('item', {
          isLE: variant.isLimitedEdition || variant.isLimitedEditionPart,
        })
      "
    >
      <VisionSwatch
        :class="bem('swatch')"
        :swatches="variant.swatches"
        :swatch-icon="variant.swatchIcon"
        :icon-overlay="variant.isIris? 'ir' : ''"
        :is-active="props.activeSwatchIndex === index"
        :mobile-size="props.mobileSize"
        :full-width="props.fullWidthSwatches"
        :badge-text="
          (props.showLimitedEditionBadge &&
            (variant.isLimitedEdition || variant.isLimitedEditionPart)) ?
            'LE' : ''"
        :lazy-load-images="props.lazyLoadSwatchIcon"
        :size="props.size"
        @select="selectSwatch(index)"
      />
    </li>
    <slot />
  </ul>
</template>
<script setup lang="ts">
const bem = useBEM('SwatchSelect')

interface Props {
  variants: Array<any>;
  scaleUp?: boolean;
  size?: 'regular' | 'medium' | 'large' | 'small';
  activeSwatchIndex?: number;
  showLimitedEditionBadge?: boolean;
  inProductCard?: boolean;
  limitedEditionCount?: number;
  lazyLoadSwatchIcon?: boolean;
  fullWidthSwatches?: boolean;
  singleLineOnTablet?: boolean;
  mobileSize?: '' | 'regular' | 'medium' | 'large';
  background?: 'default' | 'transparent';
  display?: 'grid' | 'flex'
}

const props = withDefaults(defineProps<Props>(), {
  scaleUp: false,
  size: 'regular',
  activeSwatchIndex: 0,
  showLimitedEditionBadge: false,
  inProductCard: false,
  limitedEditionCount: 0,
  lazyLoadSwatchIcon: false,
  fullWidthSwatches: true,
  singleLineOnTablet: false,
  mobileSize: '',
  background: 'default',
  display: 'grid'
});

const emits = defineEmits(['active-swatch-index-changed']);

function selectSwatch(index: number) {
  emits('active-swatch-index-changed', index);
}
</script>


<style lang="postcss">
.SwatchSelect {
  @apply grid grid-cols-6 gap-xxs
  p-4 rounded-lg bg-grey-200;
  max-width: 441px;

  .is-dark-chrome-theme & {
    @apply bg-grey-800;
    --vision-swatch-large-background-color: theme('colors.grey.800');
    --vision-swatch-large-border-color: theme('colors.grey.600');
    --vision-swatch-large-active-border-color: theme('colors.grey.600')
  }

  @screen sm {
    @apply grid-cols-7;
  }

  @screen md {
    @apply grid-cols-6;
  }

  @screen lg {
    @apply grid-cols-7;
  }

  &__slot {
    padding: 4px;
    flex-grow: 1;
  }

  &__button-container {
    @apply flex flex-col;
  }

  &--in-product-card {
    @apply flex gap-xs flex-wrap justify-items-start w-full max-w-none;
  }

  &--single-line-on-tablet {
    @apply md:grid-cols-12 max-w-full;
  }

  &--full-width-swatches {
    .SwatchSelect__item {
      @apply relative;
      padding-bottom: 100%;

      @supports (aspect-ratio: 1/1) {
        @apply aspect-square pb-0;
      }
    }
  }

  &--background-transparent {
    @apply p-0 bg-transparent
  }

  &--display-flex {
    @apply flex gap-2;
  }

  &[data-limited-count='0'] {
    & [data-item='9'] {
      @apply hidden;

      @screen sm {
        @apply block;
      }

      @screen md {
        @apply hidden;
      }

      @screen lg {
        @apply block;
      }
    }
  }

  &[data-limited-count='1'] {
    & [data-item='8'] {
      @apply hidden;

      @screen sm {
        @apply block;
      }

      @screen md {
        @apply hidden;
      }

      @screen lg {
        @apply block;
      }
    }

    & [data-item='9'] {
      @apply block;
    }
  }

  &[data-limited-count='2'] {
    & [data-item='7'] {
      @apply hidden;

      @screen sm {
        @apply block;
      }

      @screen md {
        @apply hidden;
      }

      @screen lg {
        @apply block;
      }
    }

    & [data-item='8'] {
      @apply block;
    }
  }

  &[data-limited-count='3'] {
    & [data-item='6'] {
      @apply hidden;

      @screen sm {
        @apply block;
      }

      @screen md {
        @apply hidden;
      }

      @screen lg {
        @apply block;
      }
    }

    & [data-item='7'] {
      @apply block;
    }
  }

  &[data-limited-count='4'] {
    & [data-item='5'] {
      @apply hidden;

      @screen sm {
        @apply block;
      }

      @screen md {
        @apply hidden;
      }

      @screen lg {
        @apply block;
      }
    }

    & [data-item='6'] {
      @apply block;
    }
  }

  &[data-limited-count='5'] {
    & [data-item='4'] {
      @apply hidden;

      @screen sm {
        @apply block;
      }

      @screen md {
        @apply hidden;
      }

      @screen lg {
        @apply block;
      }
    }

    & [data-item='5'] {
      @apply block;
    }
  }

  &[data-limited-count='6'] {
    & [data-item='3'] {
      @apply hidden;

      @screen sm {
        @apply block;
      }

      @screen md {
        @apply hidden;
      }

      @screen lg {
        @apply block;
      }
    }

    & [data-item='4'] {
      @apply block;
    }
  }

  &[data-limited-count='7'] {
    & [data-item='2'] {
      @apply hidden;

      @screen sm {
        @apply block;
      }

      @screen md {
        @apply hidden;
      }

      @screen lg {
        @apply block;
      }
    }

    & [data-item='3'] {
      @apply block;
    }
  }

  &[data-limited-count='8'] {
    & [data-item='1'] {
      @apply hidden;

      @screen sm {
        @apply block;
      }

      @screen md {
        @apply hidden;
      }

      @screen lg {
        @apply block;
      }
    }

    & [data-item='2'] {
      @apply block;
    }
  }

  &[data-limited-count='9'] {
    & [data-item='0'] {
      @apply hidden;

      @screen sm {
        @apply block;
      }

      @screen md {
        @apply hidden;
      }

      @screen lg {
        @apply block;
      }
    }

    & [data-item='1'] {
      @apply block;
    }
  }
}
</style>
